//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//
import React, { useCallback, useMemo, useRef } from 'react';
import { SelectValue } from 'antd/lib/select';
import cx from 'classnames';
import { debounce } from 'lodash';

import { PicklistValue } from 'components/inputs/types';
import { Text } from 'components/typography';
import AppConstants from 'utils/AppConstants';

import Select from '../Select';
import FuzzyDropdown from './components/FuzzyDropdown';
import { useFuzzySelection } from './hooks/useFuzzySelection';
import { useFuzzyState } from './hooks/useFuzzyState';
import { FuzzyOptionState, FuzzySelectProps } from './types';
import { getDisplayLabel, parseFuzzyValue } from './utils/fuzzyValueHelpers';

import './FuzzySelect.less';

/**
 * Helper: Get display value for readonly mode
 */
const getReadonlyDisplayValue = (
  value: SelectValue | undefined,
  defaultValue: SelectValue | undefined,
  values?: PicklistValue[]
): string => {
  if (!values) {
    return value?.toString() || defaultValue?.toString() || '';
  }

  const currentVal = value ?? defaultValue;
  const valStr = typeof currentVal === 'string' ? currentVal : String(currentVal || '');
  const { value: baseValue, percentage } = parseFuzzyValue(valStr);
  const matchedOption = values.find((item) => item.value === baseValue || item.value === currentVal);

  if (matchedOption) {
    if (percentage !== null) {
      if (matchedOption.hasMinMaxTypoConfig) {
        const typoText = percentage === 1 ? 'typo' : 'typos';
        return `${matchedOption.label} (Max ${percentage} ${typoText})`;
      }
      return `${matchedOption.label} (${percentage}%)`;
    }
    return matchedOption.label;
  }

  return values
    .filter((item) => item.value === currentVal)
    .map(({ label }) => label)
    .join(', ');
};

/**
 * Helper: Restore fuzzy option state when opening the dropdown
 */
const restoreFuzzyOptionState = (
  selectedValue: SelectValue | undefined,
  values: PicklistValue[] | undefined,
  optionData: PicklistValue[]
): FuzzyOptionState | null => {
  if (!selectedValue) return null;

  const valStr = typeof selectedValue === 'string' ? selectedValue : String(selectedValue);
  const { value: baseValue, percentage, typoCount } = parseFuzzyValue(valStr);

  if (percentage === null && typoCount === null) return null;

  const optionsToUse = values || optionData;
  const option = optionsToUse?.find((opt) => opt.value === baseValue);

  if (option && (option.hasFuzzyOption || option.hasMinMaxTypoConfig)) {
    return { option, percentage, typoCount };
  }

  return null;
};

/**
 * FuzzySelect is an enhanced select component that supports fuzzy matching
 * Allows users to select options with configurable matching parameters:
 * - Percentage-based fuzzy matching (0-100%)
 * - Typo tolerance (1-4 typos)
 *
 * It renders through the shared `Select` (AntD `Select`) rather than a bespoke
 * popover, so a fuzzy picklist and a plain one are the same control with the same
 * dropdown machinery - see the note on `dropdownRender` below.
 *
 * @example
 * ```tsx
 * <FuzzySelect
 *   optionData={options}
 *   value={selectedValue}
 *   onChange={handleChange}
 *   placeholder="Select an option"
 * />
 * ```
 */
const FuzzySelect: React.FC<FuzzySelectProps> = ({
  children,
  className,
  displayMode,
  optionData = [],
  placeholder = 'Select',
  useDataTypeBadges = false,
  values,
  value,
  defaultValue,
  disabled = false,
  onChange,
  onSearchChange,
  isTokenInput,
}) => {
  // Use custom hooks for state management
  const {
    isOpen,
    setIsOpen,
    searchValue,
    setSearchValue,
    selectedValue,
    setSelectedValue,
    expandedFuzzyOption,
    setExpandedFuzzyOption,
  } = useFuzzyState(value, defaultValue, values, optionData, onChange);

  const { handleOptionSelect, handleSliderChange } = useFuzzySelection(
    selectedValue,
    expandedFuzzyOption,
    setSelectedValue,
    setExpandedFuzzyOption,
    setIsOpen,
    setSearchValue,
    onChange
  );

  // Memoized display label for selected value
  const displayLabel = useMemo(() => getDisplayLabel(selectedValue, values || optionData), [
    selectedValue,
    values,
    optionData,
  ]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  // True from the moment a press lands inside the dropdown until the mouse is released, so a
  // drag can span the whole gesture. See handleDropdownMouseDown.
  const isAdjustingRef = useRef(false);

  // Dragging the fuzzy slider moves focus off the Select's search input (rc-slider focuses its
  // own handle), and rc-select closes on blur - so the dropdown vanished on the first movement
  // of the circle, committing whatever value it happened to be on. Suppress exactly that close.
  // A press *outside* the dropdown never runs this handler, so click-away still closes normally.
  const handleDropdownMouseDown = useCallback((event: React.MouseEvent) => {
    // rc-select normally guards this itself; see the note on dropdownContent below.
    event.preventDefault();

    isAdjustingRef.current = true;
    const finish = () => {
      isAdjustingRef.current = false;
      document.removeEventListener('mouseup', finish);
      // Hand focus back to the search input, otherwise the Select has nothing to blur and could
      // no longer close on click-away once a drag had moved focus to the slider handle.
      wrapperRef.current?.querySelector<HTMLInputElement>('.ant-select-search__field')?.focus();
    };
    document.addEventListener('mouseup', finish);
  }, []);

  // Handle dropdown visibility changes
  const handleVisibilityChange = useCallback(
    (visible: boolean) => {
      // rc-select defers its blur handling through a timer, so this flag is already set by the
      // time a slider-drag-induced close would arrive here.
      if (!visible && isAdjustingRef.current) {
        return;
      }
      if (!visible) {
        setExpandedFuzzyOption(null);
        setSearchValue('');
      } else if (visible) {
        const fuzzyState = restoreFuzzyOptionState(selectedValue, values, optionData);
        if (fuzzyState) {
          setExpandedFuzzyOption(fuzzyState);
        }
      }
      setIsOpen(visible);
    },
    [selectedValue, values, optionData, setExpandedFuzzyOption, setSearchValue, setIsOpen]
  );

  // Debounced search callback
  const debouncedSearchChange = useMemo(
    () =>
      debounce((searchText: string) => {
        onSearchChange?.(searchText);
      }, 300),
    [onSearchChange]
  );

  // Handle search input changes. Unlike a bare text input, clearing the search box on a
  // Select does not clear the selection - it just restores the unfiltered option list.
  const handleSearch = useCallback(
    (input: string) => {
      setSearchValue(input);
      if (!input) {
        debouncedSearchChange.cancel();
        onSearchChange?.('');
        return;
      }
      debouncedSearchChange(input);
    },
    [setSearchValue, onSearchChange, debouncedSearchChange]
  );

  // Early return for readonly mode
  if (displayMode === AppConstants.INPUT_DISPLAY_MODE.READONLY) {
    const displayValue = getReadonlyDisplayValue(value, defaultValue, values);
    return <Text>{displayValue}</Text>;
  }

  // Render dropdown content.
  //
  // The onMouseDown guard is load-bearing. rc-select normally wraps its menu in a div carrying
  // `onMouseDown: preventDefaultEvent` (rc-select/lib/DropdownMenu.js), which stops the press
  // from blurring the Select's search input. That wrapper only renders when rc-select has menu
  // items of its own - and ours has none, since the options live here instead. Without it, a
  // press on an option blurs the input, rc-select closes the dropdown, and the element unmounts
  // before `click` ever fires, so nothing is ever selected. Replicate the guard ourselves - see
  // handleDropdownMouseDown, which also keeps the dropdown open across a slider drag.
  const dropdownContent = (
    <div onMouseDown={handleDropdownMouseDown}>
      <FuzzyDropdown
        optionData={optionData}
        values={values}
        useDataTypeBadges={useDataTypeBadges}
        searchValue={searchValue}
        selectedValue={selectedValue}
        expandedFuzzyOption={expandedFuzzyOption}
        isTokenInput={isTokenInput}
        defaultValue={defaultValue}
        onOptionSelect={handleOptionSelect}
        onSliderChange={handleSliderChange}>
        {children}
      </FuzzyDropdown>
    </div>
  );

  return (
    <div
      ref={wrapperRef}
      className={cx('fuzzy-select', { 'fuzzy-select--disabled': disabled })}
      data-testid="fuzzy-select">
      <Select
        className={cx('fuzzy-select-control', className)}
        // The options live in `dropdownContent`, not in AntD `Option` children: a fuzzy option
        // expands into an inline slider panel, which is not something a menu item can hold.
        // `dropdownRender` lets us own the list's markup while AntD keeps ownership of the
        // dropdown itself - crucially its default `getPopupContainer`, which portals to
        // `document.body`. That is what a plain picklist operator already does, and why it is
        // immune to the `overflow` clipping on the filter row / group that used to cut this
        // list off in Merge Studio.
        dropdownRender={() => dropdownContent}
        dropdownClassName="fuzzy-select-dropdown-overlay"
        // `open` is controlled so the dropdown still opens even though AntD sees no options.
        open={isOpen}
        onDropdownVisibleChange={handleVisibilityChange}
        // `labelInValue` lets the closed control show our formatted label (e.g.
        // "Fuzzy Field (80%)") without needing a matching `Option` to resolve it from.
        labelInValue
        value={selectedValue ? { key: String(selectedValue), label: displayLabel } : undefined}
        showSearch
        // FuzzyDropdown does its own filtering off `searchValue`.
        filterOption={false}
        onSearch={handleSearch}
        notFoundContent={null}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
};

export default FuzzySelect;
